import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, X, RotateCcw, Check, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Photo {
  id: string;
  thumbnail_url: string;
  width: number;
  height: number;
}

interface GalleryInfo {
  id: string;
  title: string;
  description: string | null;
  event_date: string | null;
  review_status: "pending" | "in_progress" | "submitted";
  final_slug: string | null;
}

const functionUrl = (name: string) => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined;
  const baseUrl = supabaseUrl || (projectId ? `https://${projectId}.supabase.co` : "");
  return `${baseUrl}/functions/v1/${name}`;
};

const apiCall = async <T,>(name: string, body: unknown): Promise<T> => {
  const res = await fetch(functionUrl(name), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erreur");
  return data as T;
};

const GallerySelection = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState<GalleryInfo | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [rejected, setRejected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submittedSlug, setSubmittedSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const data = await apiCall<{
          gallery: GalleryInfo;
          photos: Photo[];
          rejected_ids: string[];
        }>("gallery-review-access", { token });
        setGallery(data.gallery);
        setPhotos(data.photos);
        setRejected(new Set(data.rejected_ids));
        if (data.gallery.review_status === "submitted") {
          setSubmittedSlug(data.gallery.final_slug);
        }
      } catch (e: any) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const isLocked = gallery?.review_status === "submitted";

  const toggle = async (photoId: string) => {
    if (isLocked) return;
    const willReject = !rejected.has(photoId);
    const next = new Set(rejected);
    if (willReject) next.add(photoId);
    else next.delete(photoId);
    setRejected(next);
    try {
      await apiCall("gallery-review-toggle", { token, photoId, rejected: willReject });
    } catch (e: any) {
      toast.error(e.message);
      // revert
      const revert = new Set(rejected);
      setRejected(revert);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const data = await apiCall<{ slug: string }>("gallery-review-submit", { token });
      setSubmittedSlug(data.slug);
      toast.success("Sélection envoyée !");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const keptCount = useMemo(() => photos.length - rejected.size, [photos, rejected]);

  if (loading) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!gallery) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <h1 className="text-2xl font-bold mb-2">Lien invalide</h1>
          <p className="text-muted-foreground">Ce lien de sélection n'existe pas ou a expiré.</p>
        </Card>
      </main>
    );
  }

  if (submittedSlug) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center p-4">
        <meta name="robots" content="noindex,nofollow" />
        <Card className="max-w-lg w-full p-10 text-center">
          <div className="inline-flex p-3 rounded-full bg-primary/10 mb-4">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: "var(--font-serif, Instrument Serif)" }}>
            Sélection envoyée
          </h1>
          <p className="text-muted-foreground mb-6">
            Votre galerie finale a été générée. Vous pouvez la consulter avec le même mot de passe que la galerie originale.
          </p>
          <Link to={`/galerie/${submittedSlug}`}>
            <Button size="lg" className="w-full">
              Voir ma galerie finale
            </Button>
          </Link>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background pb-32">
      <meta name="robots" content="noindex,nofollow" />

      <header className="border-b border-border py-6 px-4">
        <div className="container mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">{gallery.title}</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Cliquez sur le <strong className="text-foreground">×</strong> pour retirer les photos que vous ne voulez pas garder.
            Toutes les photos sont gardées par défaut. Quand vous êtes prêt, validez en bas de page.
          </p>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-2 md:px-6 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
          {photos.map((p) => {
            const isRejected = rejected.has(p.id);
            return (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                disabled={isLocked}
                className="relative aspect-square bg-muted rounded-md overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <img
                  src={p.thumbnail_url}
                  alt=""
                  loading="lazy"
                  className={`w-full h-full object-cover transition-all duration-200 ${
                    isRejected ? "opacity-30 grayscale" : "opacity-100"
                  }`}
                />
                {isRejected ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/40">
                    <div className="flex flex-col items-center gap-1 text-foreground">
                      <RotateCcw className="h-5 w-5 md:h-6 md:w-6" />
                      <span className="text-[10px] md:text-xs font-medium uppercase tracking-wider">Retirée</span>
                    </div>
                  </div>
                ) : (
                  <div className="absolute top-1.5 right-1.5 md:top-2 md:right-2 bg-background/90 hover:bg-destructive hover:text-destructive-foreground rounded-full p-1.5 md:p-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all shadow-md">
                    <X className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 inset-x-0 border-t border-border bg-background/95 backdrop-blur z-30">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm">
            <span className="font-mono text-lg md:text-xl font-semibold text-primary">{keptCount}</span>
            <span className="text-muted-foreground"> / {photos.length} photos gardées</span>
            {rejected.size > 0 && (
              <span className="text-muted-foreground"> · {rejected.size} retirée{rejected.size > 1 ? "s" : ""}</span>
            )}
          </div>
          <Button
            size="lg"
            onClick={() => setConfirmOpen(true)}
            disabled={keptCount === 0 || submitting || isLocked}
          >
            <Check className="h-4 w-4 mr-2" />
            Valider ma sélection
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer votre sélection</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez livrer <strong>{keptCount} photo{keptCount > 1 ? "s" : ""}</strong>
              {rejected.size > 0 && <> ({rejected.size} retirée{rejected.size > 1 ? "s" : ""})</>}.
              Cette action est <strong>irréversible</strong> — vous ne pourrez plus modifier après.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={submit} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};

export default GallerySelection;
